import { DadosDaConta } from "./Ajustes.js";
import { useCallback, useEffect, useState } from "react";
import { browserSupportsWebAuthn, startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import {
  contarLinhas,
  dataCurta,
  hojeDoUsuario,
  montarCadernoMarkdown,
  temFormatacaoDecorativa,
  nomeDoArquivo,
  type CredencialNaLista,
  type Exportacao,
} from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import {
  IconeCriar,
  IconeExcluir,
  IconeExportar,
} from "../componentes/icones.js";
import { useSessao } from "../estado/sessao.js";

/**
 * Conta — o que se mexe uma vez e não se olha mais: a saída dos dados e as
 * chaves de entrada.
 *
 * Fica fora da navegação principal de propósito. Ela nomeia o que o produto
 * faz todo dia, e nove destinos já é o limite do que cabe na fita do celular;
 * isto aqui se alcança pelos Ajustes, no início.
 */
export function Conta({ aoSair }: { aoSair: () => void }) {
  return (
    /* Sem título e sem o "Início" que sobrou de quando `/conta` era rota
       própria: depois da fusão ele era uma migalha para a home nascendo debaixo
       da fita de abas de Ajustes. */
    <div className="conta-grade">
      <div className="conta-grade__coluna">
        <DadosDaConta aoSair={aoSair} />
        <Exportar />
      </div>
      <div className="conta-grade__coluna">
        <Passkeys />
      </div>
    </div>
  );
}

type Estado =
  | { nome: "parado" }
  | { nome: "preparando" }
  | { nome: "pronto"; dados: Exportacao }
  | { nome: "erro"; mensagem: string };

/**
 * Exportação (RN-35).
 *
 * O servidor devolve o arquivo inteiro numa resposta, e os dois downloads são
 * montados aqui a partir dela — uma ida ao servidor, dois arquivos. O `.json` é
 * o dado completo; o `.md` é o caderno legível em qualquer editor.
 *
 * A tela é deliberadamente pequena: o que precisa estar certo é o arquivo.
 */
function Exportar() {
  const sessao = useSessao();
  const [estado, setEstado] = useState<Estado>({ nome: "parado" });

  const dia = hojeDoUsuario(sessao.usuario.fusoHorario);

  async function preparar() {
    setEstado({ nome: "preparando" });
    try {
      setEstado({ nome: "pronto", dados: await pedir("exportacao.tudo") });
    } catch (e) {
      setEstado({
        nome: "erro",
        mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível montar a exportação.",
      });
    }
  }

  return (
    <section className="ajustes__grupo" aria-labelledby="exportar-titulo">
      <h2 className="t-secao" id="exportar-titulo">
        Seus dados
      </h2>
      <p className="campo__ajuda">
        Dois arquivos: o <strong>JSON</strong> leva tudo — trilhas, itens, respostas, agendamentos, simulados e
        notas — e o <strong>Markdown</strong> leva o caderno, legível em qualquer editor. Nada aqui depende do
        Grifo para ser aberto depois.
      </p>

      {estado.nome !== "pronto" && (
        <button className="botao" type="button" onClick={() => void preparar()} disabled={estado.nome === "preparando"}>
          {estado.nome === "preparando" ? "Montando…" : "Preparar exportação"}
        </button>
      )}

      {estado.nome === "preparando" && (
        <p className="campo__ajuda" role="status">
          Lendo o banco inteiro. Num acervo grande isso leva alguns segundos.
        </p>
      )}

      {estado.nome === "erro" && (
        <p className="campo__erro" role="alert">
          {estado.mensagem}
        </p>
      )}

      {estado.nome === "pronto" && <Pronta dados={estado.dados} dia={dia} aoRefazer={() => void preparar()} />}
    </section>
  );
}

function Pronta({ dados, dia, aoRefazer }: { dados: Exportacao; dia: string; aoRefazer: () => void }) {
  const contagens = contarLinhas(dados).filter((c) => c.linhas > 0);
  const total = contagens.reduce((soma, c) => soma + c.linhas, 0);
  // RN-39: cor, realce, tamanho, alinhamento, sublinhado e tachado não têm
  // equivalente em Markdown e somem no arquivo. O aviso vem **antes** de gerar,
  // não depois — descobrir a perda abrindo o arquivo é descobrir tarde demais.
  const comPerda = dados.notas.filter((n) => temFormatacaoDecorativa(n.html)).length;

  return (
    <>
      {/*
        O resumo antes do download, e não depois: é como se percebe que a
        exportação veio vazia ou pela metade **antes** de guardar o arquivo
        achando que o backup está feito.
      */}
      <p role="status">
        <span className="dado">{total}</span> linhas em <span className="dado">{contagens.length}</span>{" "}
        tabelas, incluindo <span className="dado">{dados.notas.length}</span> nota(s).
      </p>

      <ul className="lista-dados">
        {contagens.map((c) => (
          <li className="linha linha--espalhada" key={c.tabela}>
            <span className="etiqueta">{c.tabela}</span>
            <span className="dado lista-dados__valor">{c.linhas}</span>
          </li>
        ))}
      </ul>

      {comPerda > 0 && (
        <p className="campo__ajuda" role="status">
          <strong>
            {comPerda} de {dados.notas.length} nota(s)
          </strong>{" "}
          têm formatação que o Markdown não sabe escrever — cor, realce, tamanho,
          alinhamento, sublinhado ou tachado. No arquivo do caderno o texto sai
          inteiro e essa formatação some. O <strong>JSON</strong> leva o HTML
          como está guardado, sem perda nenhuma.
        </p>
      )}

      <div className="linha">
        <Baixar
          nome={nomeDoArquivo("json", dia)}
          tipo="application/json"
          conteudo={() => `${JSON.stringify(dados, null, 2)}\n`}
          rotulo="Baixar o JSON"
        />
        <Baixar
          nome={nomeDoArquivo("md", dia)}
          tipo="text/markdown"
          conteudo={() => montarCadernoMarkdown(dados)}
          rotulo="Baixar o caderno"
        />
      </div>

      <button className="botao botao--secundario botao--compacto" type="button" onClick={aoRefazer}>
        Montar de novo
      </button>
    </>
  );
}

/**
 * O arquivo é escrito no navegador a partir do que já está na memória.
 *
 * O conteúdo é montado no clique, não antes: guardar o JSON serializado no
 * estado dobraria a memória de uma exportação grande sem ninguém pedir.
 */
function Baixar({
  nome,
  tipo,
  conteudo,
  rotulo,
}: {
  nome: string;
  tipo: string;
  conteudo: () => string;
  rotulo: string;
}) {
  return (
    <button
      className="botao"
      type="button"
      onClick={() => {
        const url = URL.createObjectURL(new Blob([conteudo()], { type: `${tipo};charset=utf-8` }));
        const a = document.createElement("a");
        a.href = url;
        a.download = nome;
        // No documento antes do clique: o Safari do iOS ignora clique em
        // elemento que não está na árvore, e é justamente lá que este botão
        // precisa funcionar.
        document.body.appendChild(a);
        a.click();
        a.remove();
        // O blob fica preso na memória da aba até alguém soltar, e uma
        // exportação grande é bem grande. Mas soltar **no mesmo tique** cancela
        // o download que acabou de começar no Safari: espera-se um minuto.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }}
    >
      <Icone glifo={IconeExportar} papel="acao" />
      {rotulo}
    </button>
  );
}

// ------------------------------------------------------------------ passkey

type EstadoDasChaves =
  | { nome: "carregando" }
  | { nome: "pronto"; credenciais: CredencialNaLista[] }
  | { nome: "erro"; mensagem: string };

/**
 * Aparelhos que entram sem senha (RN-36).
 *
 * **A senha não sai daqui.** Esta seção só acrescenta caminhos; não há nada
 * nela que desligue o login por senha, e é de propósito: sem recuperação por
 * e-mail, uma conta cuja única entrada mora dentro de um aparelho é uma conta a
 * uma perda de distância de ficar trancada.
 *
 * Cadastrar acontece **no aparelho que se quer cadastrar** — a chave nasce ali
 * e não viaja. É por isso que o texto insiste nisso: quem cadastra só o celular
 * e depois perde o celular fica com a senha, e mais nada.
 */
function Passkeys() {
  const [estado, setEstado] = useState<EstadoDasChaves>({ nome: "carregando" });
  const [apelido, setApelido] = useState("");
  const [cadastrando, setCadastrando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const suportado = browserSupportsWebAuthn();

  const carregar = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      const { credenciais } = await pedir("passkey.listar");
      setEstado({ nome: "pronto", credenciais });
    } catch (e) {
      setEstado({
        nome: "erro",
        mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível ler os aparelhos.",
      });
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function cadastrar() {
    setCadastrando(true);
    setAviso(null);
    try {
      const { desafioId, opcoes } = await pedir("passkey.opcoesRegistro");
      // O aparelho abre a biometria aqui. Se a pessoa cancelar, a promessa
      // rejeita — e cancelar não é erro: é uma resposta.
      const resposta = await startRegistration({
        optionsJSON: opcoes as unknown as PublicKeyCredentialCreationOptionsJSON,
      });
      const { credenciais } = await pedir("passkey.registrar", {
        corpo: { desafioId, apelido: apelido.trim() || "Este aparelho", resposta: resposta as never },
      });
      setEstado({ nome: "pronto", credenciais });
      setApelido("");
    } catch (e) {
      const nome = e instanceof Error ? e.name : "";
      setAviso(
        nome === "NotAllowedError" || nome === "AbortError"
          ? "O cadastro foi cancelado no aparelho."
          : e instanceof FalhaDaApi
            ? e.message
            : "Este aparelho não completou o cadastro. A senha continua funcionando.",
      );
    } finally {
      setCadastrando(false);
    }
  }

  async function remover(id: string) {
    setAviso(null);
    try {
      const { credenciais } = await pedir("passkey.excluir", { params: { id } });
      setEstado({ nome: "pronto", credenciais });
      setConfirmando(null);
    } catch (e) {
      setAviso(e instanceof FalhaDaApi ? e.message : "Não foi possível remover.");
    }
  }

  return (
    <section className="ajustes__grupo" aria-labelledby="passkey-titulo">
      <h2 className="t-secao" id="passkey-titulo">
        Aparelhos
      </h2>
      <p className="campo__ajuda">
        Uma passkey entra com a digital ou o rosto, sem digitar nada. Ela nasce{" "}
        <strong>no aparelho em que você a cadastra</strong> e não sai de lá — então cadastre o celular{" "}
        <em>e</em> o computador. <strong>A senha continua valendo em qualquer um dos dois</strong>, e é ela que
        te tira de um aperto se um aparelho se perder.
      </p>

      {!suportado && (
        <p className="campo__ajuda" role="status">
          Este navegador não faz passkey. Continue entrando com a senha — ou cadastre por outro aparelho.
        </p>
      )}

      {estado.nome === "carregando" && (
        <div aria-busy="true">
          <span className="so-leitor" role="status">
            Carregando os aparelhos
          </span>
          <div className="esqueleto esqueleto--faixa" />
        </div>
      )}

      {estado.nome === "erro" && (
        <div className="estado estado--erro" role="alert">
          <p className="estado__texto">{estado.mensagem}</p>
          <button className="botao" type="button" onClick={() => void carregar()}>
            Tentar de novo
          </button>
        </div>
      )}

      {estado.nome === "pronto" && estado.credenciais.length === 0 && (
        <p className="campo__ajuda">
          Nenhum aparelho cadastrado ainda. Cadastre este aqui embaixo; depois repita no outro aparelho, entrando
          nele com a senha.
        </p>
      )}

      {estado.nome === "pronto" && estado.credenciais.length > 0 && (
        <ul className="pilha">
          {estado.credenciais.map((c) => (
            <li className="linha linha--espalhada" key={c.id}>
              <span>
                <strong>{c.apelido}</strong>
                <br />
                <span className="etiqueta">
                  {c.tipoDispositivo === "multiDevice" ? "sincroniza entre aparelhos" : "só neste aparelho"} ·
                  cadastrado em {dataCurta(c.criadoEm)}
                  {c.ultimoUso ? ` · usado em ${dataCurta(c.ultimoUso)}` : " · nunca usado"}
                </span>
              </span>
              {/*
                Dois toques para remover (invariante 11). Digitar o nome fica
                para lote e trilha, que apagam conteúdo: aqui só se perde um
                atalho de entrada, e a senha continua atrás.
              */}
              {confirmando === c.id ? (
                <span className="linha">
                  <button className="botao botao--compacto" type="button" onClick={() => void remover(c.id)}>
                    <Icone glifo={IconeExcluir} papel="acao" tamanho="p" />
                    Confirmar
                  </button>
                  <button
                    className="botao botao--secundario botao--compacto"
                    type="button"
                    onClick={() => setConfirmando(null)}
                  >
                    Cancelar
                  </button>
                </span>
              ) : (
                <button
                  className="botao botao--secundario botao--compacto"
                  type="button"
                  onClick={() => setConfirmando(c.id)}
                >
                  Remover
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {suportado && estado.nome === "pronto" && (
        <div className="campo">
          <label className="campo__rotulo" htmlFor="apelido">
            Nome deste aparelho
          </label>
          <input
            id="apelido"
            className="campo__entrada"
            type="text"
            maxLength={60}
            placeholder="iPhone, PC de casa…"
            value={apelido}
            onChange={(e) => setApelido(e.target.value)}
          />
          <p className="campo__ajuda">É por este nome que você reconhece o que remover depois.</p>
          <button className="botao" type="button" disabled={cadastrando} onClick={() => void cadastrar()}>
            <Icone glifo={IconeCriar} papel="acao" />
            {cadastrando ? "Falando com o aparelho…" : "Cadastrar este aparelho"}
          </button>
        </div>
      )}

      {aviso && (
        <p className="campo__erro" role="alert">
          {aviso}
        </p>
      )}
    </section>
  );
}
